import React from 'react';
import {
  TextField, MenuItem, FormControlLabel, Switch, Autocomplete, Chip, Stack, Typography, Box,
} from '@mui/material';

// Field definitions come from the API (vendorOfferingFields), keyed by service category.
export function fieldsForCategory(fieldDefs, category) {
  if (!fieldDefs || !category) return [];
  return fieldDefs[category] || fieldDefs._default || [];
}

// Form inputs for the category-specific details of a vendor offering.
export function OfferingDetailInputs({ fields, values, onChange }) {
  const set = (key, value) => onChange({ ...values, [key]: value });

  return (
    <Stack spacing={2}>
      {fields.map(f => {
        const v = values[f.key];
        const label = f.required ? `${f.label} *` : f.label;
        switch (f.type) {
          case 'multiselect':
            return (
              <Autocomplete
                key={f.key}
                multiple
                options={f.options || []}
                value={Array.isArray(v) ? v : []}
                onChange={(_, next) => set(f.key, next)}
                renderTags={(tags, getTagProps) => tags.map((t, i) => <Chip size="small" label={t} {...getTagProps({ index: i })} key={t} />)}
                renderInput={params => <TextField {...params} label={label} size="small" />}
              />
            );
          case 'select':
            return (
              <TextField key={f.key} select size="small" label={label} value={v ?? ''} onChange={e => set(f.key, e.target.value)} fullWidth>
                {(f.options || []).map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
              </TextField>
            );
          case 'number':
            return (
              <TextField
                key={f.key} size="small" type="number" label={label} fullWidth
                value={v ?? ''}
                inputProps={{ min: 0 }}
                onChange={e => set(f.key, e.target.value === '' ? null : Number(e.target.value))}
              />
            );
          case 'boolean':
            return (
              <FormControlLabel
                key={f.key}
                control={<Switch checked={Boolean(v)} onChange={e => set(f.key, e.target.checked)} />}
                label={f.label}
              />
            );
          case 'textarea':
            return <TextField key={f.key} size="small" label={label} value={v ?? ''} onChange={e => set(f.key, e.target.value)} fullWidth multiline rows={3} />;
          default:
            return <TextField key={f.key} size="small" label={label} value={v ?? ''} onChange={e => set(f.key, e.target.value)} fullWidth />;
        }
      })}
    </Stack>
  );
}

// Client-side check mirroring the API's validation, so problems show before saving.
export function missingRequiredDetails(fields, values) {
  return fields
    .filter(f => f.required)
    .filter(f => {
      const v = values[f.key];
      return v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
    })
    .map(f => f.label);
}

// Read-only list of an offering's details, labelled per the category's field definitions.
export function OfferingDetailList({ fields, details }) {
  const shown = fields.filter(f => {
    const v = details?.[f.key];
    return !(v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0));
  });
  if (!shown.length) return null;
  return (
    <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(140px, 40%) 1fr' }, columnGap: 2, rowGap: 0.5 }}>
      {shown.map(f => {
        const v = details[f.key];
        let text = v;
        if (Array.isArray(v)) text = v.join(', ');
        else if (typeof v === 'boolean') text = v ? 'Yes' : 'No';
        else if (f.type === 'number' && /₹|price/i.test(f.label)) text = `₹${Number(v).toLocaleString('en-IN')}`;
        return (
          <React.Fragment key={f.key}>
            <Typography component="dt" variant="caption" color="text.secondary">{f.label}</Typography>
            <Typography component="dd" variant="body2" sx={{ m: 0, whiteSpace: 'pre-line' }}>{String(text)}</Typography>
          </React.Fragment>
        );
      })}
    </Box>
  );
}

export function formatPriceRange(o) {
  const fmt = n => `₹${Number(n).toLocaleString('en-IN')}`;
  const range = o.priceTo != null && Number(o.priceTo) !== Number(o.priceFrom)
    ? `${fmt(o.priceFrom)} – ${fmt(o.priceTo)}`
    : `From ${fmt(o.priceFrom)}`;
  return `${range} ${o.priceUnit}`;
}
